import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Status } from "../../shared/schedule.ts";

/** Shared primitives. */

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: string;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__icon" aria-hidden="true">
        {icon}
      </div>
      <div className="empty__title">{title}</div>
      {body ? <p className="empty__body">{body}</p> : null}
      {action}
    </div>
  );
}

export function AlertBanner({ children }: { children: ReactNode }) {
  return (
    <div className="banner" role="alert">
      <span aria-hidden="true">⚠️</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Hard deletes confirm by typing the thing's own name — deleting a room takes
 * its jobs and their whole history with it.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  requireTypedConfirmation,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  requireTypedConfirmation?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input, button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const ready = !requireTypedConfirmation || typed.trim() === requireTypedConfirmation;

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        <p>{body}</p>
        {requireTypedConfirmation ? (
          <label className="field" style={{ marginBottom: "var(--space-5)" }}>
            <span className="field__label">Type “{requireTypedConfirmation}” to confirm</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </label>
        ) : null}
        <div className="btn-row" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--danger" disabled={!ready} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_WORD: Record<Status, string> = {
  ok: "Done",
  never: "Never done",
  due: "Due",
  overdue: "Overdue",
};

/** How late a job is, in words — the pill that used to carry this is gone. */
export function statusWord(status: Status, daysLate: number, daysUntil: number | null): string {
  if (status === "overdue") return `${daysLate} ${daysLate === 1 ? "day" : "days"} late`;
  if (status === "ok" && daysUntil !== null) return `due in ${daysUntil}d`;
  return STATUS_WORD[status];
}

/** Mutually-exclusive options as one connected control. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  hint,
  label,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  hint?: string;
  label?: string;
}) {
  return (
    <div>
      <div className="segmented" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="segmented__option"
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {hint ? <div className="segmented__hint">{hint}</div> : null}
    </div>
  );
}
